import { getAuthHeaders, getAxios } from '@/helpers/axios-helper';

import { TitleListResponse, TitleListName } from '@/interfaces/responses/title-list';
import { TitleObjectType, Title, NewWatchlistTitle } from '@/interfaces/titles';
import { WatchlistTitleGraphqlFragment } from '@/pages/graphql/fragments/WatchlistTitle.fragment';

export interface SeenSeason {
	season_number: number;
	seen_episode_numbers: number[];
}

export type TrackingRunningSeasonValue = '' | '0' | '1';

export const WatchListHelper = {
	/**
	 * Helper function which returns the resource url to the given object type
	 * and id
	 */
	getObjectUrl(watchlistName: TitleListName, objectType: TitleObjectType, objectId: number, locale: string) {
		if (!watchlistName) throw new Error(`Expected "watchlistName" to be defined. ${watchlistName}`);
		if (!objectType) throw new Error(`Expected "objectType" to be defined. ${objectType}`);
		if (!objectId) throw new Error(`Expected "objectId" to be a defined number. ${objectId}`);
		return `${JW_CONFIG.WRITE_URL}/title_list/${watchlistName}/object_type/${objectType}/object_id/${objectId}/${locale}`;
	},

	/**
	 * Fetch a title list using the given justwatch_id
	 */
	async fetch(justwatch_id: string, accessToken: string): Promise<TitleListResponse> {
		const url = `${JW_CONFIG.WRITE_URL}/title_list/search`;
		const params = { justwatch_id };
		const body = {
			technical_names: ['WATCHLIST', 'SEENLIST', 'MORELIST', 'LIKELIST', 'DISLIKELIST'],
			include_entries: true,
		};
		const headers = await getAuthHeaders(accessToken);

		return getAxios()
			.post(url, body, { params, headers })
			.then(response => response.data);
	},

	/**
	 * fetch the given title to the given watchlist
	 */
	async fetchItem(
		justwatch_id: string,
		accessToken: string,
		watchlistName: TitleListName,
		objectType: TitleObjectType,
		objectId: number,
		locale: string
	): Promise<any> {
		// don't fetch personalized data when jwId is not available
		if (!justwatch_id) {
			return Promise.resolve([]);
		}

		const url = this.getObjectUrl(watchlistName, objectType, objectId, locale);
		const params = { justwatch_id };
		const headers = await getAuthHeaders(accessToken);

		return getAxios()
			.get(url, { params, headers })
			.then(response => response.data);
	},

	/**
	 * adds the given title to the given watchlist
	 */
	async add(
		justwatch_id: string,
		accessToken: string,
		watchlistName: TitleListName,
		objectType: TitleObjectType,
		objectId: number,
		locale: string,
		body?: any
	): Promise<any> {
		const url = this.getObjectUrl(watchlistName, objectType, objectId, locale);
		const params = { justwatch_id };
		const headers = await getAuthHeaders(accessToken);

		return getAxios()
			.put(url, body, { params, headers })
			.then(response => true);
	},

	/**
	 * Remove a single item from the watchlist
	 */
	async remove(
		justwatch_id: string,
		accessToken: string,
		watchlistName: TitleListName,
		objectType: TitleObjectType,
		objectId: number,
		locale: string,
		body?: any
	): Promise<any> {
		const url = this.getObjectUrl(watchlistName, objectType, objectId, locale);
		const params = { justwatch_id };
		const headers = await getAuthHeaders(accessToken);

		const data = body;
		return getAxios()
			.delete(url, { data, params, headers })
			.then(response => true);
	},

	/**
	 *
	 * @param {NewWatchlistTitle} title can be either an object_type movie/show
	 *
	 * @returns {string} '1' for running season, '0' for complete, '' for no season/movies
	 */
	handleTrackingValue(title: NewWatchlistTitle): TrackingRunningSeasonValue {
		let value: TrackingRunningSeasonValue = '';
		if ('seasons' in title && title.seasons?.length) {
			if ('remaining_season_episode_count' in title) {
				value = title.remaining_season_episode_count ? '1' : '0';
			}
		}

		return value;
	},

	handleTrackingValueGraphql(title: WatchlistTitleGraphqlFragment['node']): TrackingRunningSeasonValue {
		let value: TrackingRunningSeasonValue = '';
		if (title.__typename === 'Show' && title.totalSeasonCount) {
			value = title.seenState.progress < 100 ? '1' : '0';
		}

		return value;
	},

	seasonNumber(title: Title | NewWatchlistTitle) {
		if (title.seasons && title.seasons.length > 0) {
			return title.seasons[0].season_number;
		}
		return null;
	},
};
